import { Component, Inject, OnInit } from '@angular/core';
import { FormControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { BillingService, Purchase } from '@galaxy/billing';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import * as moment from 'moment';
import { finalize, take } from 'rxjs/operators';

export type Action = 'pause' | 'resume';

interface DunningData {
  purchase: Purchase;
  action: Action;
}

@Component({
  selector: 'billing-ui-dunning-dialog',
  templateUrl: './dunning-dialog.component.html',
  styleUrls: ['./dunning-dialog.component.scss'],
  standalone: false,
})
export class DunningDialogComponent implements OnInit {
  action: Action;
  purchase: Purchase;
  loading = false;
  minDate: Date;

  formGroup: UntypedFormGroup;

  get pauseDunningUntil(): FormControl<Date | null> {
    return this.formGroup.get('pauseDunningUntil') as FormControl<Date>;
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) private data: DunningData,
    private dialogRef: MatDialogRef<DunningDialogComponent>,
    private alertService: SnackbarService,
    private billingService: BillingService,
    private formBuilder: UntypedFormBuilder,
  ) {
    this.formGroup = this.formBuilder.group({
      pauseDunningUntil: ['pauseDunningUntil', Validators.required],
    });

    this.minDate = this.setDateToTomorrow();
  }

  ngOnInit(): void {
    this.action = this.data.action;
    this.purchase = this.data.purchase;
  }

  private setDateToTomorrow(): Date {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow;
  }

  pauseDunning(): void {
    this.loading = true;
    const day = moment.utc(this.purchase.timestamp).format('YYYYMMDD');

    if (this.formGroup.valid) {
      this.billingService
        .pauseDunning(this.purchase.merchantId, this.purchase.purchaseId, day, this.pauseDunningUntil.value)
        .pipe(
          take(1),
          finalize(() => (this.loading = false)),
        )
        .subscribe(
          () => {
            this.dialogRef.close(true);
            this.alertService.openSuccessSnack('Dunning paused');
          },
          () => this.alertService.openErrorSnack('Failed to pause dunning'),
        );
    } else {
      this.alertService.openErrorSnack('Form is not valid');
    }
  }

  resumeDunning(): void {
    this.loading = true;

    const day = moment.utc(this.purchase.timestamp).format('YYYYMMDD');
    this.billingService
      .resumeDunning(this.purchase.merchantId, this.purchase.purchaseId, day)
      .pipe(
        take(1),
        finalize(() => (this.loading = false)),
      )
      .subscribe(
        () => {
          this.dialogRef.close(true);
          this.alertService.openSuccessSnack('Dunning resumed');
        },
        () => this.alertService.openErrorSnack('Failed to resume dunning'),
      );
  }
}
